import React, { useCallback, useState, useEffect, useMemo } from "react";
import styles from "./styles.module.scss";
import Member from "./member";
import Button from "../../../../components/new-button";
import Search from "../../../../components/SearchInput/SearchInput";
import useCommonStore from "../../../../stores/common";
import AddMemberModal from "./add-member-modal";
import Plus from "../../../../assets/svg/icons/common/Plus";
import ResetPasswordModal from "./reset-password-modal";
import RemoveMember from "./delete-modal";
import useOrganizationStore from "../../../../stores/organization";
import { SearchOrganizationMembers, UpdateMember } from "../../../../../api";
import toast from "react-hot-toast";
import { debounce } from "lodash";
import Spinner from "../../../../components/Loaders/Spinner";

const Members = () => {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMemberData, setSelectedMemberData] = useState(null);
  const members = useOrganizationStore(
    (state) => state.selectedOrganizationData?.users,
  );
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationData?.organization?.id,
  );
  const setSelectedOrganizationData = useOrganizationStore(
    (state) => state.setSelectedOrganizationData,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [removeMemberModalOpen, setRemoveMemberModalOpen] = useState(false);

  const debouncedSearchMembers = useMemo(
    () =>
      debounce(async (query) => {
        if (!query || query.length < 1) {
          setSearchResults([]);
          setIsSearching(false);
          return;
        }

        setIsSearching(true);
        try {
          const response = await SearchOrganizationMembers(
            selectedOrganizationId,
            {
              q: query,
            },
          );
          setSearchResults(response.users || []);
        } catch (error) {
          console.error(error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300),
    [selectedOrganizationId],
  );

  useEffect(() => {
    return () => {
      debouncedSearchMembers.cancel();
    };
  }, [debouncedSearchMembers]);

  const handleSearchTextChange = (e) => {
    setSearchQuery(e.target.value);

    if (e.target.value.length >= 1) {
      setIsSearching(true);
      debouncedSearchMembers(e.target.value);
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  const handleAddMember = () => {
    setSelectedMemberData(null);
    setAddMemberModalOpen(true);
  };

  const handleEditMember = (member) => {
    setSelectedMemberData(member);
    setAddMemberModalOpen(true);
  };

  const updateMemberStatus = (member, status) => {
    let tempName = member.name.split(" ");
    let firstName = "";
    let lastName = "";
    if (tempName.length > 1) {
      lastName = tempName[tempName.length - 1];
      tempName.pop();
      firstName = tempName.toString().replace(/,/g, " ");
    } else {
      firstName = tempName[0];
    }
    member["disabled"] = status;
    member["first_name"] = firstName;
    member["last_name"] = lastName !== "" ? lastName : "placeholder";

    UpdateMember(selectedOrganizationId, member)
      .then((response) => {
        if (response.success) {
          setSelectedOrganizationData((prev) => ({
            ...prev,
            users: response.users,
          }));
          toast.success("Member status updated successfully");
        } else {
          toast.error(response.message);
          console.error(response.message);
        }
      })
      .catch((error) => console.log(error));
  };

  const displayedMembers = searchQuery.length >= 1 ? searchResults : members;

  return (
    <div
      className={`${styles.members} ${expandDetails ? styles.expanded : ""}`}
    >
      <div className={styles.header}>
        <Search
          placeholder="Search"
          value={searchQuery}
          onChange={handleSearchTextChange}
        />
        <Button variant="primary" onClick={handleAddMember}>
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Plus /> Add member
          </span>
        </Button>
      </div>
      <div className={styles.membersList}>
        {isSearching ? (
          <div className={styles.loading}>
            <Spinner />
          </div>
        ) : displayedMembers && displayedMembers.length > 0 ? (
          displayedMembers.map((member, index) => (
            <React.Fragment key={`member-${member.id}`}>
              <Member
                viewType={expandDetails ? "card" : "list"}
                member={member}
                onStatusChange={updateMemberStatus}
                onResetPassword={(memberId) => {
                  setSelectedMember(memberId);
                  setResetPasswordModalOpen(true);
                }}
                onEdit={(member) => {
                  setSelectedMemberData(member);
                  setAddMemberModalOpen(true);
                }}
                onRemove={(memberId) => {
                  setSelectedMember(memberId);
                  setRemoveMemberModalOpen(true);
                }}
              />
              {!expandDetails && index !== displayedMembers.length - 1 && (
                <span className={styles.divider} />
              )}
            </React.Fragment>
          ))
        ) : (
          ""
        )}

        {!isSearching &&
          searchQuery.length >= 1 &&
          searchResults.length === 0 && (
            <div className={styles.noResults}>
              <p>No results found</p>
            </div>
          )}
      </div>

      {addMemberModalOpen && (
        <AddMemberModal
          open={addMemberModalOpen}
          onClose={() => {
            setAddMemberModalOpen(false);
            setSelectedMemberData(null);
          }}
          isEdit={selectedMemberData !== null}
          selectedMember={selectedMemberData}
        />
      )}

      <RemoveMember
        open={removeMemberModalOpen}
        memberId={selectedMember}
        onClose={() => {
          setRemoveMemberModalOpen(false);
          setSelectedMember(null);
        }}
      />

      <ResetPasswordModal
        open={resetPasswordModalOpen}
        memberId={selectedMember}
        onClose={() => {
          setResetPasswordModalOpen(false);
          setSelectedMember(null);
        }}
      />
    </div>
  );
};

export default Members;
